import { Link } from "@remix-run/react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@core/api";
import { useCurrentUser } from "@core/hooks/useCurrentUser";

/**
 * True when the current account has ResourceCenterEnabled=true
 * (admin account-detail toggle → Customer tag).
 */
export function useResourceCenterEnabled(): boolean {
  const { user } = useCurrentUser();
  const api = useApi();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["spg", "resource-center-enabled", user?.CustomerId ?? null],
    queryFn: () => api.GetSPGResourceCenterEnabled(),
    staleTime: 60_000,
    enabled: !!user?.Id,
  });
  if (!user?.Id) {
    return false;
  }
  if (isLoading || isError) {
    return false;
  }
  return data?.Enabled === true;
}

/**
 * Footer / nav link to Resource Center — rendered only when the account toggle is on.
 * Wrap in ClientOnly when used from SSR layouts that need auth before the query runs.
 */
export function ResourceCenterNavLink({ className, label, href }: { className?: string; label: string; href: string }) {
  const enabled = useResourceCenterEnabled();
  if (!enabled) {
    return null;
  }
  return (
    <Link to={href} className={className} data-testid="nav-resource-center">
      {label}
    </Link>
  );
}
